import {
    Module,
    VuexModule,
    Action,
    getModule,
    Mutation,
} from 'vuex-module-decorators';
import store from '~common-store';
import { HomeState, HomeData, Home } from '~/interfaces/home.interface';
import { OwnerModule } from './owners';
import { HomesSecurityModule } from './homesSecurity';
import { HomeSecurity } from '~/interfaces/homeSecurity.interface';
import { HomesEnergyModule } from './homesEnergy';
import { HomeEnergy } from '~/interfaces/homeEnergy.interface';
import { DevicesModule } from './devices';
import { RoomsModule } from './rooms';

@Module({ dynamic: true, store, name: 'homes', namespaced: true })
class Homes extends VuexModule implements HomeState {
    currentHomeId: string | undefined = '';
    homes: Home [] = [];


    get currentHome() {
        return this.homes.find((home) => home.id === this.currentHomeId);
    }

    get hasSecurity() {
        return HomesSecurityModule.homesSecurity.find((home) => home.homeId === this.currentHomeId);
    }

    get hasEnergy() {
        return HomesEnergyModule.homesEnergy.find((home) => home.homeId === this.currentHomeId);
    }

    get homeInfo() {
        if (!this.currentHome) {
            return {};
        } else {
            return [
                {
                    key: 'city',
                    value: this.currentHome.city,
                    type: 'string',
                    tooltip : 'Home city (can be wrong if city is determined from IP)'
                },
                {
                    key: 'country',
                    value: this.currentHome.country,
                    type: 'string',
                    tooltip: 'Home country (can be wrong if country is determined from IP)'
                },
                { key: 'name', value: this.currentHome.name },
                {
                    key: 'timezone',
                    value: this.currentHome.timezone,
                    tooltip: 'Home timezone'
                },
                {
                    key: 'location',
                    value: this.currentHome.coordinates
                        ? this.currentHome.coordinates[1] + ' ' + this.currentHome.coordinates[0]
                        : undefined,
                    tooltip: 'Home location (GPS coordinates)'
                },
            ];
        }
    }

    @Action
    async setCurrentHomeId(homeId: string | undefined) {
        if (homeId) {
            OwnerModule.getOwners({ homeId });
        }
        this.context.commit('SET_CURRENT_HOME_ID', homeId);
    }

    @Mutation
    SET_CURRENT_HOME_ID(homeId: string | undefined) {
        this.currentHomeId = homeId;
    }

    @Action
    async setHome(homeData: HomeData) {
        // Commit devices
        if (homeData.devices) {
            DevicesModule.setDevices({devices: homeData.devices, homeId: homeData.id});
        }

        if (homeData.settingssecurity) {
            HomesSecurityModule.ADD_HOME_SECURITY(
                {
                    ...homeData.settingssecurity,
                    homeId: homeData.id,
                } as HomeSecurity,
            );
        }

        if (homeData.settingsenergy) {
            HomesEnergyModule.ADD_HOME_ENERGY(
                {
                    ...homeData.settingsenergy,
                    homeId: homeData.id,
                } as HomeEnergy,
            );
        }

        if (homeData.rooms) {
            const rooms = homeData.rooms.map((room) => ({ ...room, homeId: homeData.id }));
            RoomsModule.SET_ROOMS(rooms);
        }
    }

    @Action
    async setHomes(homes: HomeData[]) {
        const homesToset: Home [] = [] as Home [];
        homes.forEach(async (home: HomeData) => {
            const newHome: Home = {
                id: home.id,
                name: home.name,
                city: home.city,
                country: home.country,
                timezone: home.timezone,
                coordinates: home.coordinates,
                attachments: home.attachments
            };
            homesToset.push(newHome);
            await this.setHome(home);
        });
        this.context.commit('SET_HOMES', homesToset);
    }

    @Mutation
    SET_HOMES(homes: Home[]) {
        this.homes = homes;
    }

    @Action
    reset() {
        this.context.commit('RESET_HOMES');
    }

    @Mutation
    RESET_HOMES() {
        this.homes = [];
        this.currentHomeId = undefined;
    }
}

export const HomesModule = getModule(Homes);
